import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { FaUserPlus } from "react-icons/fa";
import InputField from "components/form/InputField";
import SelectField from "components/form/SelectField";
import TextAreaField from "components/form/TextAreaField";
import DateField from "components/form/DateField";
import FormActionButtons from "components/Button/FormActionButtons";
import PasswordPopup from "components/modal/PasswordPopup";
import { getAllPartnerTypes } from "../../../../services/partnerTypeService";
import { getAllRoles } from "../../../../services/roleService";
import { updatePartner } from "../../../../services/userServices/partnerService";
import { getUserById } from "../../../../services/userServices/userService";
import { getAllMemberStatus } from "../../../../services/memberStatusService";
import { getAllSuppliers } from "../../../../services/supplierService";
// Helper: Generate password acak (8 karakter alfanumerik)
const generatePassword = () => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from({ length: 8 }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length))
  ).join("");
};

const toOptions = (arr) =>
  arr.map((label) => ({
    value: label.toLowerCase().replace(/\s+/g, "-"),
    label,
  }));

const EditMitra = () => {
  const navigate = useNavigate();
  const [jenisMitra, setJenisMitra] = useState("");
  const [fullPartnerId, setFullPartnerId] = useState("");
  const [userName, setUserName] = useState("");
  const [showUserForm, setShowUserForm] = useState(false);

  // options dari API
  const [jenisMitraOptions, setJenisMitraOptions] = useState([]);
  const [loadingJenisMitra, setLoadingJenisMitra] = useState(true);
  const [jenisPenggunaOptions, setJenisPenggunaOptions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [statusKeanggotaanOptions, setStatusKeanggotaanOptions] = useState([]);
  const [loadingStatusKeanggotaan, setLoadingStatusKeanggotaan] =
    useState(true);

  const [showPopup, setShowPopup] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [isSaveSuccess, setIsSaveSuccess] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm();
  const watchNama = watch("nama");
  const watchEmail = watch("email");

  // status anggota tetap dummy seperti diminta
  const statusOptions = toOptions(["AKTIF", "TIDAK_AKTIF"]);

  const mitra = JSON.parse(localStorage.getItem("editMitra") || "{}");
  const userId = mitra?.id;

  // Fetch data dan reset form
  useEffect(() => {
    let mounted = true;

    const fetchAllData = async () => {
      try {
        // Ambil data user login
        const rawUserInfo = localStorage.getItem("userInfo");
        const storedUser =
          rawUserInfo && rawUserInfo !== "undefined"
            ? JSON.parse(rawUserInfo)
            : {};
        if (!userId) {
          navigate("/data-mitra");
          return;
        }

        // Fetch roles dan partner types
        const [rolesRes, partnerTypesRes, memberStatusRes] = await Promise.all([
          getAllRoles(),
          getAllPartnerTypes(),
          getAllMemberStatus(),
        ]);
        if (!mounted) return;

        // Filter roles hanya status PARTNER
        const filteredRoles = (rolesRes || [])
          .filter((r) => String(r.status).toUpperCase() === "PARTNER")
          .map((r) => ({ value: r.id, label: r.name }));
        setJenisPenggunaOptions(filteredRoles);

        const filteredMemberStatus = (memberStatusRes || [])
          .filter((ms) => String(ms.status).toUpperCase() === "PARTNER")
          .map((ms) => ({ value: ms.id, label: ms.name }));

        setStatusKeanggotaanOptions(filteredMemberStatus);

        // Format partner types options
        const partnerOptions = (partnerTypesRes || []).map((t) => ({
          value: t.id,
          label: t.name,
          code: t.code,
          count: t._count?.partners ?? 0,
        }));
        setJenisMitraOptions(partnerOptions);

        // Fetch suppliers
        const suppliersRes = await getAllSuppliers();
        if (!mounted) return;
        const supplierOpts = (suppliersRes || []).map((s) => ({
          value: s.id,
          label: s.name,
        }));
        setSupplierOptions(supplierOpts);
        setLoadingSuppliers(false);

        // Fetch detail user by id
        const userRes = await getUserById(userId);
        const user = userRes?.data;

        const partnerIdFull = user.partners?.partner_id || ""; // misal "MA_A0003"
        setFullPartnerId(partnerIdFull);
        setJenisMitra(user.partners?.partner_type_id || "");
        setUserName(storedUser?.name || "");

        reset({
          nama: user.profiles?.full_name || "",
          email: user.email || "",
          telp: user.profiles?.phone || "",
          alamat: user.profiles?.address || "",
          KodePos: user.partners?.postalcode || "",
          DiInputOleh: storedUser?.name || "",
          JenisMitra: user.partners?.partner_type_id || "",
          jenisPengguna: user.profiles?.roles?.[0]?.id || "",
          IDMitra: partnerIdFull || "",
          NamaIDMitra: "", // nanti set via useEffect di bawah
          statusKeanggotaan: user.partners?.member_status_id || "",
          status: user.partners?.active_status?.toLowerCase() || "-",
          supplier: user.partners?.supplier_id || "",
          TanggalJadiMitra:
            user.partners?.partner_join_date?.substring(0, 10) || "",
        });
      } catch (err) {
        console.error("Gagal memuat data:", err);
        navigate("/data-mitra");
      } finally {
        setLoadingJenisMitra(false);
        setLoadingRoles(false);
      }
    };

    fetchAllData();

    return () => {
      mounted = false;
    };
  }, [navigate, reset, userId]);

  // Gabungkan nama + fullPartnerId untuk NamaIDMitra
  useEffect(() => {
    const namaUser = (watchNama || "").trim();
    const gabungan = namaUser
      ? `${namaUser}/${fullPartnerId}`
      : fullPartnerId || "";

    setValue("NamaIDMitra", gabungan);
  }, [watchNama, fullPartnerId, setValue]);

  const onSubmit = async (data) => {
    try {
      const prevEmail = mitra?.email || "";
      const payload = {
        fullName: data.nama,
        //email: data.email,
        phone: data.telp,
        partnerTypeId: data.JenisMitra,
        roleId: data.jenisPengguna,
        partnerId: data.NamaIDMitra.split("/")[1],
        nameIdPartner: data.NamaIDMitra,
        postalCode: data.KodePos,
        partnerJoinDate: data.TanggalJadiMitra,
        memberStatusId: data.statusKeanggotaan,
        active_status: (data.status || "").toUpperCase(),
        supplierId: data.supplier,
        address: data.alamat,
        updatedBy: data.DiInputOleh,
        userType: "PARTNER",
      };

      // Tambahkan email hanya jika diisi
      if (data.email) {
        payload.email = data.email;

        // Jika sebelumnya belum ada email → generate password & show popup
        if (!prevEmail) {
          payload.password = generatePassword();
          setGeneratedPassword(payload.password);
          setShowPopup(true);
        }
      }

      console.log("Payload untuk update:", payload);
      await updatePartner(userId, payload); // pastikan pakai userId
      if (!showUserForm) navigate("/data-mitra");
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Gagal mengupdate mitra");
    }
  };

  return (
    <div className="pl-64 mt-8">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="max-w-4xl p-6 mx-auto bg-white border border-gray-200 shadow-lg rounded-2xl"
      >
        <h2 className="mb-6 text-xl font-semibold text-center text-gray-800 sm:text-2xl">
          Edit Data Mitra
        </h2>

        {/* Toggle Buat Akun User */}
        {!watchEmail && (
          <div className="flex justify-end mb-4">
            <button
              type="button"
              onClick={() => setShowUserForm((prev) => !prev)}
              className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white rounded-lg shadow-md 
        transition-colors duration-200 
        ${
          showUserForm
            ? "bg-red-500 hover:bg-red-600"
            : "bg-green-600 hover:bg-green-700"
        }`}
            >
              <FaUserPlus size={12} />
              {showUserForm ? "Batalkan" : "Buat Akun"}
            </button>
          </div>
        )}

        {/* Grid Form Data Mitra */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Kolom Kiri */}
          <div className="space-y-4">
            <InputField
              label="Nama"
              {...register("nama", { required: "Nama wajib diisi" })}
              error={errors.nama}
            />

            <SelectField
              label="Jenis Pengguna"
              control={control}
              options={jenisPenggunaOptions}
              isLoading={loadingRoles}
              {...register("jenisPengguna", {
                required: "Jenis pengguna wajib diisi",
              })}
              error={errors.jenisPengguna}
            />
            <SelectField
              label="Status Keanggotaan"
              control={control}
              options={statusKeanggotaanOptions}
              isLoading={loadingStatusKeanggotaan}
              {...register("statusKeanggotaan", {
                required: "Status keanggotaan wajib diisi",
              })}
              error={errors.statusKeanggotaan}
            />

            <InputField
              label="ID Mitra"
              readOnly
              {...register("IDMitra", { required: true })}
              error={errors.IDMitra}
            />

            <SelectField
              label="Supplier"
              control={control}
              options={supplierOptions}
              isLoading={loadingSuppliers}
              {...register("supplier", {
                required: "Supplier wajib dipilih",
              })}
              error={errors.supplier}
            />

            {/* Form User (muncul hanya jika toggle aktif) */}
            {(watchEmail || showUserForm) && (
              <div>
                <InputField
                  label="Email"
                  type="email"
                  {...register("email", {
                    required:
                      showUserForm || !watchEmail ? "Email wajib diisi" : false,
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Format email tidak valid",
                    },
                  })}
                  error={errors.email}
                />
                <input type="hidden" {...register("password")} />
              </div>
            )}

            <TextAreaField
              label="Alamat"
              rows={3}
              {...register("alamat", { required: "Alamat wajib diisi" })}
              error={errors.alamat}
            />
          </div>

          {/* Kolom Kanan */}
          <div className="space-y-4">
            <InputField
              label="No. Telp"
              {...register("telp", {
                required: "No. Telp wajib diisi",
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Hanya angka yang diperbolehkan",
                },
                minLength: {
                  value: 10,
                  message: "Minimal 10 digit",
                },
                maxLength: {
                  value: 13,
                  message: "Maksimal 13 digit",
                },
              })}
              error={errors.telp}
            />
            <SelectField
              label="Status Mitra"
              control={control}
              options={statusOptions}
              {...register("status", {
                required: "Status wajib diisi",
              })}
              error={errors.StatusKeanggotaan}
            />
            <SelectField
              label="Jenis Mitra"
              control={control}
              options={jenisMitraOptions}
              isLoading={loadingJenisMitra}
              {...register("JenisMitra", {
                required: "Jenis Mitra wajib diisi",
                onChange: (e) => setJenisMitra(e.target.value),
              })}
              error={errors.JenisMitra}
            />
            <InputField
              label="Nama ID Mitra"
              readOnly
              {...register("NamaIDMitra", { required: true })}
              error={errors.NamaIDMitra}
            />
            <InputField
              label="Kode Pos"
              {...register("KodePos", { required: "Kode Pos wajib diisi" })}
              error={errors.KodePos}
            />
            <DateField
              label="Tanggal Jadi Mitra"
              type="date"
              {...register("TanggalJadiMitra", {
                required: "Tanggal wajib diisi",
              })}
              error={errors.TanggalJadiMitra}
            />
            <InputField
              label="Diinput Oleh"
              readOnly
              defaultValue={userName}
              {...register("DiInputOleh", { required: true })}
              error={errors.DiInputOleh}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <FormActionButtons
          submitLabel="Simpan"
          cancelLabel="Batal"
          onCancel={() => navigate("/data-mitra")}
          className="mt-8"
        />
        {showPopup && (
          <PasswordPopup
            password={generatedPassword}
            onClose={() => {
              setShowPopup(false);
              navigate("/data-mitra");
            }}
          />
        )}
      </form>
    </div>
  );
};

export default EditMitra;
